import { ClientAvatarType } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import GenericBlurredPlaceholder from "@/app/(components)/generics/BlurredPlaceholder/BlurredPlaceholder";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import {
  useCanEditContent,
  useCanRunPaidActions,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { updateAvatarField } from "@/app/actions/client-avatar/update/action";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { AvatarField, UserClientAvatar } from "@/app/types/client-avatar/UserClientAvatar";
import { PageParams } from "@/app/types/common/DefaultProps";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { QueryKey } from "@/app/vars/QueryKeys";
import { AVATAR_TYPE_BADGE_CLASS, AVATAR_TYPE_LABEL, openAvatarDetail } from "../Avatars.logic";
import styles from "../Avatars.module.css";
import AvatarCardMenu from "./AvatarCardMenu";
import AvatarFreshnessBadge from "./AvatarFreshnessBadge/AvatarFreshnessBadge";
import AvatarInitials from "./AvatarInitials";

type Props = {
  params: PageParams;
  avatar: UserClientAvatar;
  isPlaceholder?: boolean;
  isAnalyzing?: boolean;
  isStale?: boolean;
  onExtract?: (targetType: ClientAvatarType, isEnrich: boolean) => void;
};

export default function SmallCard(props: Props) {
  const T = useT();
  const canEdit = useCanEditContent();
  const canRunPaidActions = useCanRunPaidActions();
  const { params, avatar, isPlaceholder, isAnalyzing = false, isStale = false, onExtract } = props;
  const isExtracted = avatar.id !== -1;

  const router = useRouter();
  const queryClient = useQueryClient();

  const handleSaveName = async (val: string) => {
    try {
      await updateAvatarField(avatar.offerId, avatar.type, AvatarField.NAME, val);
      queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_AVATAR, avatar.offerId] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error("Failed to update avatar name", error);
    }
  };

  const handleSaveRole = async (val: string) => {
    try {
      await updateAvatarField(avatar.offerId, avatar.type, AvatarField.ROLE, val);
      queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_AVATAR, avatar.offerId] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.MESSAGE_SEQUENCE] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROSPECT_SEARCH_SUMMARY] });
      router.refresh();
    } catch (error) {
      console.error("Failed to update avatar role", error);
    }
  };

  return (
    <div
      className={UStyles.classNames(styles.smallCard, isPlaceholder && styles.noHover)}
      onClick={
        isPlaceholder ? undefined : () => openAvatarDetail(avatar, T, undefined, isAnalyzing)
      }
      style={{ cursor: isPlaceholder ? "default" : "pointer" }}
    >
      <div className={styles.cardHeader}>
        <span className={styles[AVATAR_TYPE_BADGE_CLASS[avatar.type]]}>
          {T(AVATAR_TYPE_LABEL[avatar.type])}
        </span>
        {!isPlaceholder && (
          <div className={styles.cardHeaderActions}>
            {isExtracted && canRunPaidActions && (
              <AvatarFreshnessBadge
                isStale={isStale}
                onEnrich={onExtract ? () => onExtract(avatar.type, true) : undefined}
              />
            )}
            <AvatarCardMenu params={params} avatar={avatar} onExtract={onExtract} />
          </div>
        )}
      </div>
      <div className={styles.smallRow}>
        {isPlaceholder ? (
          <div className={styles.avatarSm} />
        ) : (
          <AvatarInitials name={avatar.name} type={avatar.type} />
        )}
        <div className={styles.smallMeta} style={isPlaceholder ? { flex: 1, minWidth: 0 } : {}}>
          {isPlaceholder ? (
            <GenericBlurredPlaceholder
              className={styles.genericPlaceholder}
              rowClassName={styles.genericRow}
              rows={[
                { cells: [{ width: "45%" }] },
                { cells: [{ width: "35%" }] },
                { cells: [{ width: "100%" }] },
              ]}
            />
          ) : (
            <>
              <EditableText
                value={avatar.name}
                onSave={handleSaveName}
                onEditClick={() => openAvatarDetail(avatar, T, AvatarField.NAME, isAnalyzing)}
                className={styles.smallName}
                placeholder={T(TKey.CLIENT_AVATAR_CARD_NAME_PLACEHOLDER)}
                maxLength={Limits.MAX_AVATAR_SHORT_TEXT}
                disabled={isAnalyzing || !canEdit}
              />
              <EditableText
                value={avatar.role}
                onSave={handleSaveRole}
                onEditClick={() => openAvatarDetail(avatar, T, AvatarField.ROLE, isAnalyzing)}
                className={styles.smallRole}
                placeholder={T(TKey.CLIENT_AVATAR_CARD_ROLE_PLACEHOLDER)}
                maxLength={Limits.MAX_AVATAR_SHORT_TEXT}
                disabled={isAnalyzing || !canEdit}
              />
              {avatar.snapshotSummary && (
                <p className={styles.smallSummary}>{avatar.snapshotSummary}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
